import { useEffect, useRef, useState } from "react";
import { ProfileProvider, useProfile } from "./state/ProfileContext";
import { bootstrapRuntime } from "./runtime";
import { Intake } from "./components/Intake";
import { PasswordGate } from "./components/PasswordGate";
import { AdminPage } from "./components/AdminPage";
import { secrets_loaded } from "./lib/secrets_boot";
import { is_admin_url } from "./lib/access_api";
import { HeatmapScreen } from "./components/HeatmapScreen";
import { CompareScreen } from "./components/CompareScreen";
import { LoadingLine, Banner } from "./ui/common";
import { AiCostBadge } from "./components/AiCostBadge";
import { candidate_times_need_regeneration, full_reset_keep_birth_events } from "./lib/rectification";
import { search_span_seconds, FINE_DIVISIONAL_MAX_SPAN_SECONDS } from "./lib/scoring";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** ISO "YYYY-MM-DD" → "14 Jul 1991" (profile strip). */
function fmtBirthDate(iso?: string): string {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d || m < 1 || m > 12) return String(iso);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

function Session() {
  const { user_profile, run_data, runOp, reload, mutate, exitUser, syncError } = useProfile();
  const [busy, setBusy] = useState<string | null>(null);
  // A failed auto-advance — shown as an error banner (NOT the blocking spinner) with a Retry
  // button; bumping the nonce re-runs the effect for the same (stage+evidence) signature.
  const [advanceError, setAdvanceError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  // The marked-times comparison page (opened from the heatmap's "★ Compare times" chip) —
  // a sibling full-page view over the same profile; Back returns to the heatmap.
  const [compareOpen, setCompareOpen] = useState(false);
  // Guards the heavy auto-advance against React StrictMode's double-invoke and any
  // re-render: a given (stage+evidence) signature is launched at most once.
  const inFlight = useRef<string | null>(null);

  const stage = (run_data?.stage as string) ?? "answering_questions";

  // Auto-advance the engine for the two "work" stages, exactly like render_dynamic_page.
  useEffect(() => {
    if (!user_profile || !run_data) return;
    const regenerate = candidate_times_need_regeneration(user_profile);
    const matching = stage === "ready_internal_matching";
    if (!regenerate && !matching) return;

    const sig = `${stage}|${user_profile.updated_at}|${JSON.stringify(user_profile.search)}|${regenerate}`;
    if (inFlight.current === sig) return; // already running/ran this exact work
    inFlight.current = sig;
    setAdvanceError(null);

    // A committed rising sign means "time this window" even if the sign's window is wider than
    // FINE_DIVISIONAL_MAX_SPAN_SECONDS (high-latitude signs can rise for >2.5h) — so DON'T skip the
    // exact-time scan in that case, or the page would hang on the Level-0 view with one sign.
    const wide =
      user_profile.rising_sign_locked !== true &&
      search_span_seconds(user_profile.search ?? {}) > FINE_DIVISIONAL_MAX_SPAN_SECONDS;

    (async () => {
      if (regenerate) {
        setBusy("Recalculating stale candidate times from the current events…");
        await runOp("regenerate_internal_candidates");
        setBusy(null);
        return;
      }
      // Rising-sign stage (wide window): the heatmap is built from rising_sign_breakdown, NOT the
      // candidate scan, so skip the expensive full-day scan and show it immediately. The exact-time
      // scan runs only once a rising sign is committed (the window narrows to ~2h).
      if (wide) {
        mutate((_p, r) => {
          r.stage = "showing_internal_times";
        });
        return;
      }
      setBusy("Timing the chosen window to the second…");
      await runOp("run_internal_matching");
      setBusy(null);
      reload();
    })().catch((e) => {
      setBusy(null); // never leave the blocking spinner up on an error
      setAdvanceError(String(e?.message ?? e));
      inFlight.current = null; // allow a retry after an error
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, user_profile?.updated_at, JSON.stringify(user_profile?.search), retryNonce]);

  if (!user_profile || !run_data) return null;

  function resetEverything() {
    if (confirm("Reset everything except birth details and events?")) {
      mutate((p, r) => full_reset_keep_birth_events(p, r));
      reload();
    }
  }

  const birth = (user_profile.birth ?? {}) as any;
  const initial = String(user_profile.first_name ?? "?").trim().charAt(0).toUpperCase() || "?";
  const countOpts = (obj: any) =>
    Object.values(obj ?? {}).reduce<number>((n, v) => n + (Array.isArray(v) ? v.length : v ? 1 : 0), 0);
  const evidenceCount =
    (user_profile.events?.length ?? 0) + countOpts(user_profile.traits) + countOpts(user_profile.rejected_traits);
  const enteredTime =
    birth.time_certainty && birth.time_certainty !== "unknown" && birth.entered_time
      ? `${birth.time_certainty} · ${String(birth.entered_time).slice(0, 5)}`
      : "unknown · searching full range";

  return (
    <main className="app-main">
      {/* ── The Lagna Navigator header ── */}
      <header className="lagna-header">
        <div className="lagna-brand">
          <div className="logo-mark" aria-hidden>
            <div className="ring" />
            <div className="star" />
          </div>
          <div>
            <div className="eyebrow">Birth-Time Rectification</div>
            <div className="display-title">The Lagna Navigator</div>
          </div>
        </div>
        <div className="toolbar-actions">
          <button className="mono" onClick={exitUser}>
            ← Switch person
          </button>
          <button className="mono" onClick={resetEverything} style={{ color: "var(--danger-ink)" }}>
            ↺ Reset
          </button>
        </div>
      </header>

      {/* ── Profile strip ── */}
      <div className="profile-strip">
        <div className="profile-id">
          <div className="profile-avatar">{initial}</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>{user_profile.first_name}</div>
            <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--ink3)" }}>subject profile</div>
          </div>
        </div>
        <div className="profile-sep" />
        <div className="profile-field">
          <div className="k">Born</div>
          <div className="v">{fmtBirthDate(birth.date)}</div>
        </div>
        <div className="profile-sep" />
        <div className="profile-field">
          <div className="k">Place</div>
          <div className="v">
            {birth.lat != null && birth.lon != null
              ? `${Number(birth.lat).toFixed(2)}°, ${Number(birth.lon).toFixed(2)}°`
              : "—"}
          </div>
        </div>
        <div className="profile-sep" />
        <div className="profile-field">
          <div className="k">Entered time</div>
          <div className="v" style={{ color: "var(--ink2)" }}>{enteredTime}</div>
        </div>
        <div className="profile-live">
          <span className="pulse-dot" />
          <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--ink2)" }}>
            {evidenceCount} evidence signal{evidenceCount === 1 ? "" : "s"} live
          </span>
          {/* Running AI spend for this person (hidden until the first AI call). */}
          <AiCostBadge />
        </div>
      </div>

      {syncError && (
        <Banner kind="warn">
          Couldn’t save to the cloud: {syncError.message} — your latest change is held in this
          browser and will retry on the next edit.
        </Banner>
      )}
      {advanceError && (
        <Banner kind="error">
          Couldn’t finish the scan: {advanceError}{" "}
          <button
            className="mono"
            onClick={() => {
              setAdvanceError(null);
              inFlight.current = null;
              setRetryNonce((n) => n + 1);
            }}
          >
            ↻ Retry
          </button>
        </Banner>
      )}
      {busy && <LoadingLine message={busy} />}
      {!busy && !advanceError && stage === "ready_internal_matching" && (
        <Banner kind="info">Preparing the candidate scan…</Banner>
      )}
      {/* The heatmap page is THE landing page for a loaded profile, whatever its stage —
          intake → straight here; events/traits are added from its panels. The marked-times
          comparison page swaps in over it when opened. */}
      {!busy &&
        stage !== "ready_internal_matching" &&
        (compareOpen ? (
          <CompareScreen onBack={() => setCompareOpen(false)} />
        ) : (
          <HeatmapScreen onOpenCompare={() => setCompareOpen(true)} />
        ))}
    </main>
  );
}

function Root() {
  const { activeUser } = useProfile();
  const [ready, setReady] = useState(false);
  const [bootError, setBootError] = useState<string | null>(null);

  useEffect(() => {
    bootstrapRuntime()
      .then(() => setReady(true))
      .catch((e) => setBootError(e?.message ? String(e.message) : String(e)));
  }, []);

  if (bootError) {
    return (
      <main className="app-main">
        <Banner kind="error">Failed to load the astronomy engine: {bootError}</Banner>
      </main>
    );
  }
  if (!ready) {
    return (
      <main className="app-main">
        <LoadingLine message="Loading the Swiss Ephemeris engine…" />
      </main>
    );
  }
  return activeUser ? <Session /> : <Intake />;
}

export default function App() {
  // Secrets (OpenAI key, AWS credentials) are fetched from the Access API behind a startup
  // password — nothing else can run until they are installed.
  const [unlocked, setUnlocked] = useState(secrets_loaded());
  // A `#<password>_admin` link unlocks AND lands on the admin (user-management) page.
  const [adminMode, setAdminMode] = useState(is_admin_url());
  if (!unlocked) return <PasswordGate onReady={() => setUnlocked(true)} />;
  if (adminMode) return <AdminPage onExit={() => setAdminMode(false)} />;
  return (
    <ProfileProvider>
      <Root />
    </ProfileProvider>
  );
}
